import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { User, Save, Ruler, Scale, Trash2, Plus, Target, Calendar, Bell, BellOff, BellRing } from "lucide-react";
import {
  loadReminderPrefs,
  saveReminderPrefs,
  notificationPermission,
  requestNotificationPermission,
  startReminderScheduler,
  stopReminderScheduler,
  testNotification,
} from "@/lib/reminders";
import ChangePasswordCard from "@/components/ChangePasswordCard";
import ManageSubscriptionCard from "@/components/ManageSubscriptionCard";

const LEVELS = [
  { key: "principiante", label: "PRINCIPIANTE" },
  { key: "intermedio", label: "INTERMEDIO" },
  { key: "avanzato", label: "AVANZATO" },
];

const SEXES = [
  { key: "M", label: "M" },
  { key: "F", label: "F" },
  { key: "X", label: "X" },
];

const GOALS = [
  { key: "perdita_peso", label: "PERDITA PESO" },
  { key: "gambe", label: "GAMBE" },
  { key: "cardio", label: "FIATO/CARDIO" },
  { key: "tonificazione", label: "TONIFICAZIONE" },
];

const EQUIPMENT = [
  { key: "dumbbells", label: "MANUBRI" },
  { key: "kettlebell", label: "KETTLEBELL" },
  { key: "bands", label: "ELASTICI" },
  { key: "bodyweight", label: "CORPO LIBERO" },
];

export default function Profile() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [measurements, setMeasurements] = useState([]);
  const [showAddM, setShowAddM] = useState(false);
  const [rem, setRem] = useState(loadReminderPrefs());
  const [permission, setPermission] = useState(notificationPermission());

  const [nm, setNm] = useState({
    weight_kg: "", waist_cm: "", hips_cm: "", chest_cm: "", arm_cm: "", thigh_cm: "", notes: "",
  });

  useEffect(() => {
    api.get("/profile").then((r) => setP(r.data));
    api.get("/measurements").then((r) => setMeasurements(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...p };
      // normalise numeric fields
      ["age", "height_cm", "weight_kg", "weight_goal_kg", "sessions_per_week", "session_duration_min"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null) payload[k] = null;
        else payload[k] = Number(payload[k]);
      });
      const r = await api.put("/profile", payload);
      setP(r.data);
      toast.success("Profilo aggiornato");
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const addMeasurement = async () => {
    const payload = {};
    for (const k of Object.keys(nm)) {
      const v = nm[k];
      if (k === "notes") payload[k] = v;
      else if (v !== "" && v != null) payload[k] = Number(v);
    }
    if (Object.keys(payload).filter((k) => k !== "notes").length === 0) {
      toast.error("Inserisci almeno un valore");
      return;
    }
    try {
      const r = await api.post("/measurements", payload);
      setMeasurements([...measurements, r.data]);
      setNm({ weight_kg: "", waist_cm: "", hips_cm: "", chest_cm: "", arm_cm: "", thigh_cm: "", notes: "" });
      setShowAddM(false);
      // if weight was set, refresh profile
      if (payload.weight_kg) {
        const rp = await api.get("/profile");
        setP(rp.data);
      }
      toast.success("Misurazione registrata");
    } catch {
      toast.error("Errore");
    }
  };

  const delMeasurement = async (id) => {
    if (!window.confirm("Eliminare questa misurazione?")) return;
    try {
      await api.delete(`/measurements/${id}`);
      setMeasurements(measurements.filter((m) => m.id !== id));
    } catch {
      toast.error("Errore");
    }
  };

  const toggle = (field, val) => {
    const arr = new Set(p[field] || []);
    arr.has(val) ? arr.delete(val) : arr.add(val);
    setP({ ...p, [field]: Array.from(arr) });
  };

  if (!p) return <div className="max-w-4xl mx-auto p-8 text-[#A0A0AB]">Caricamento...</div>;

  const bmi = p.height_cm && p.weight_kg
    ? (Number(p.weight_kg) / Math.pow(Number(p.height_cm) / 100, 2)).toFixed(1)
    : null;
  const bmiCat = bmi ? (bmi < 18.5 ? "SOTTOPESO" : bmi < 25 ? "NORMALE" : bmi < 30 ? "SOVRAPPESO" : "OBESO") : null;

  const weightHist = measurements.filter((m) => m.weight_kg).map((m) => ({ date: m.date, peso: m.weight_kg }));
  const waistHist = measurements.filter((m) => m.waist_cm).map((m) => ({ date: m.date, vita: m.waist_cm }));

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-2">
        <User className="w-3 h-3" /> Scheda atleta
      </div>
      <h1 className="font-display text-5xl sm:text-6xl font-black uppercase tracking-tighter leading-[0.9] mb-8">
        Il tuo <span className="text-[#FF3B30]">profilo</span>
      </h1>

      {/* Anagrafica + BMI */}
      <div className="border border-[#2A2A35] p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Scale className="w-4 h-4 text-[#FF3B30]" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB]">Anagrafica</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Field label="Età" value={p.age ?? ""} onChange={(v) => setP({ ...p, age: v })} suffix="anni" testid="p-age" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Sesso</div>
            <div className="flex gap-1">
              {SEXES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setP({ ...p, sex: s.key })}
                  data-testid={`p-sex-${s.key}`}
                  className={`flex-1 py-2 text-xs font-bold border transition-all ${
                    p.sex === s.key ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#2A2A35] text-[#A0A0AB] hover:border-white"
                  }`}
                >{s.label}</button>
              ))}
            </div>
          </div>
          <Field label="Altezza" value={p.height_cm ?? ""} onChange={(v) => setP({ ...p, height_cm: v })} suffix="cm" testid="p-height" />
          <Field label="Peso" value={p.weight_kg ?? ""} onChange={(v) => setP({ ...p, weight_kg: v })} suffix="kg" testid="p-weight" />
          <Field label="Peso obiettivo" value={p.weight_goal_kg ?? ""} onChange={(v) => setP({ ...p, weight_goal_kg: v })} suffix="kg" testid="p-goal" />
          {bmi && (
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">BMI</div>
              <div className="flex items-baseline gap-3">
                <span className="font-display text-4xl font-black text-[#E2FF31]">{bmi}</span>
                <span className="text-xs uppercase tracking-widest text-[#A0A0AB]">{bmiCat}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Preferenze allenamento */}
      <div className="border border-[#2A2A35] p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-4 h-4 text-[#FF3B30]" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB]">Preferenze allenamento</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Livello</div>
            <div className="grid grid-cols-1 gap-1">
              {LEVELS.map((l) => (
                <button
                  key={l.key}
                  onClick={() => setP({ ...p, level: l.key })}
                  data-testid={`p-level-${l.key}`}
                  className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border transition-all ${
                    p.level === l.key ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#2A2A35] text-[#A0A0AB] hover:border-white"
                  }`}
                >{l.label}</button>
              ))}
            </div>
          </div>
          <Field label="Sessioni/settimana" value={p.sessions_per_week ?? ""} onChange={(v) => setP({ ...p, sessions_per_week: v })} suffix="a sett." testid="p-spw" />
          <Field label="Durata sessione" value={p.session_duration_min ?? ""} onChange={(v) => setP({ ...p, session_duration_min: v })} suffix="min" testid="p-dur" />
        </div>

        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2 flex items-center gap-1"><Target className="w-3 h-3" /> Obiettivi</div>
          <div className="flex flex-wrap gap-2">
            {GOALS.map((g) => (
              <button
                key={g.key}
                onClick={() => toggle("goals", g.key)}
                data-testid={`p-goal-${g.key}`}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border transition-all ${
                  (p.goals || []).includes(g.key) ? "bg-[#E2FF31] border-[#E2FF31] text-black" : "border-[#2A2A35] text-[#A0A0AB] hover:border-white"
                }`}
              >{g.label}</button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Attrezzatura disponibile</div>
          <div className="flex flex-wrap gap-2">
            {EQUIPMENT.map((e) => (
              <button
                key={e.key}
                onClick={() => toggle("equipment", e.key)}
                data-testid={`p-eq-${e.key}`}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border transition-all ${
                  (p.equipment || []).includes(e.key) ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#2A2A35] text-[#A0A0AB] hover:border-white"
                }`}
              >{e.label}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Infortuni / limitazioni</div>
            <textarea
              value={p.injuries || ""}
              onChange={(e) => setP({ ...p, injuries: e.target.value })}
              data-testid="p-injuries"
              rows={2}
              className="w-full bg-[#141419] border border-[#2A2A35] px-3 py-2 focus:border-[#FF3B30] focus:outline-none text-sm"
              placeholder="Es. Mal di schiena lombare, ginocchio sx sensibile..."
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Note per il coach</div>
            <textarea
              value={p.notes || ""}
              onChange={(e) => setP({ ...p, notes: e.target.value })}
              data-testid="p-notes"
              rows={2}
              className="w-full bg-[#141419] border border-[#2A2A35] px-3 py-2 focus:border-[#FF3B30] focus:outline-none text-sm"
              placeholder="Preferisco allenamenti mattutini..."
            />
          </div>
        </div>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="save-profile"
        className="mb-10 bg-[#FF3B30] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#e02e24] transition-all flex items-center gap-2 disabled:opacity-50"
      >
        <Save className="w-4 h-4" /> {saving ? "Salvo..." : "Salva profilo"}
      </button>

      {/* Misurazioni */}
      <div className="border border-[#2A2A35] p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Ruler className="w-4 h-4 text-[#FF3B30]" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB]">Misurazioni corporee</div>
          </div>
          <button
            onClick={() => setShowAddM(!showAddM)}
            data-testid="toggle-add-m"
            className="border border-[#2A2A35] hover:border-[#E2FF31] hover:text-[#E2FF31] text-xs font-bold uppercase tracking-widest px-3 py-1.5 flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Nuova
          </button>
        </div>

        {showAddM && (
          <div className="border border-[#2A2A35] p-4 mb-6 bg-[#141419]/40">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
              <Field label="Peso" value={nm.weight_kg} onChange={(v) => setNm({ ...nm, weight_kg: v })} suffix="kg" testid="m-weight" />
              <Field label="Vita" value={nm.waist_cm} onChange={(v) => setNm({ ...nm, waist_cm: v })} suffix="cm" testid="m-waist" />
              <Field label="Fianchi" value={nm.hips_cm} onChange={(v) => setNm({ ...nm, hips_cm: v })} suffix="cm" testid="m-hips" />
              <Field label="Petto" value={nm.chest_cm} onChange={(v) => setNm({ ...nm, chest_cm: v })} suffix="cm" testid="m-chest" />
              <Field label="Braccio" value={nm.arm_cm} onChange={(v) => setNm({ ...nm, arm_cm: v })} suffix="cm" testid="m-arm" />
              <Field label="Coscia" value={nm.thigh_cm} onChange={(v) => setNm({ ...nm, thigh_cm: v })} suffix="cm" testid="m-thigh" />
            </div>
            <div className="flex gap-2">
              <button
                onClick={addMeasurement}
                data-testid="save-m"
                className="bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-widest px-4 py-2 hover:bg-[#e02e24]"
              >Salva misurazione</button>
              <button
                onClick={() => setShowAddM(false)}
                className="border border-[#2A2A35] text-[#A0A0AB] text-xs font-bold uppercase tracking-widest px-4 py-2 hover:border-white hover:text-white"
              >Annulla</button>
            </div>
          </div>
        )}

        {/* Graph */}
        {(weightHist.length > 1 || waistHist.length > 1) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {weightHist.length > 1 && (
              <div className="border border-[#2A2A35] p-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-3">Peso nel tempo</div>
                <div className="h-40">
                  <ResponsiveContainer>
                    <LineChart data={weightHist}>
                      <CartesianGrid stroke="#2A2A35" vertical={false} />
                      <XAxis dataKey="date" stroke="#A0A0AB" tick={{ fontSize: 9 }} />
                      <YAxis stroke="#A0A0AB" tick={{ fontSize: 9 }} domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ background: "#141419", border: "1px solid #2A2A35" }} />
                      <Line type="monotone" dataKey="peso" stroke="#FF3B30" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
            {waistHist.length > 1 && (
              <div className="border border-[#2A2A35] p-4">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB] mb-3">Vita nel tempo</div>
                <div className="h-40">
                  <ResponsiveContainer>
                    <LineChart data={waistHist}>
                      <CartesianGrid stroke="#2A2A35" vertical={false} />
                      <XAxis dataKey="date" stroke="#A0A0AB" tick={{ fontSize: 9 }} />
                      <YAxis stroke="#A0A0AB" tick={{ fontSize: 9 }} domain={["auto", "auto"]} />
                      <Tooltip contentStyle={{ background: "#141419", border: "1px solid #2A2A35" }} />
                      <Line type="monotone" dataKey="vita" stroke="#E2FF31" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        )}

        {measurements.length === 0 ? (
          <div className="text-center py-8 text-[#A0A0AB] text-sm">
            Nessuna misurazione. Aggiungi la prima per tracciare i progressi.
          </div>
        ) : (
          <div className="border border-[#2A2A35] divide-y divide-[#2A2A35]">
            {measurements.slice().reverse().map((m) => (
              <div key={m.id} className="p-3 flex items-center justify-between gap-4 text-sm" data-testid={`m-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">{m.date}</div>
                  <div className="text-white flex flex-wrap gap-x-4 gap-y-1 mt-1 font-mono-alt text-xs">
                    {m.weight_kg && <span>Peso: <b>{m.weight_kg}</b>kg</span>}
                    {m.waist_cm && <span>Vita: <b>{m.waist_cm}</b>cm</span>}
                    {m.hips_cm && <span>Fianchi: <b>{m.hips_cm}</b>cm</span>}
                    {m.chest_cm && <span>Petto: <b>{m.chest_cm}</b>cm</span>}
                    {m.arm_cm && <span>Braccio: <b>{m.arm_cm}</b>cm</span>}
                    {m.thigh_cm && <span>Coscia: <b>{m.thigh_cm}</b>cm</span>}
                  </div>
                </div>
                <button
                  onClick={() => delMeasurement(m.id)}
                  data-testid={`del-m-${m.id}`}
                  className="border border-[#2A2A35] hover:border-[#EF4444] hover:text-[#EF4444] p-2 transition-all"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reminders */}
      <RemindersCard
        rem={rem}
        setRem={setRem}
        permission={permission}
        setPermission={setPermission}
      />

      {/* Subscription */}
      <ManageSubscriptionCard />

      {/* Change password */}
      <ChangePasswordCard />
    </div>
  );
}

const DAYS_IT = [
  { key: 1, label: "LUN" }, { key: 2, label: "MAR" }, { key: 3, label: "MER" },
  { key: 4, label: "GIO" }, { key: 5, label: "VEN" }, { key: 6, label: "SAB" }, { key: 0, label: "DOM" },
];

function RemindersCard({ rem, setRem, permission, setPermission }) {
  const supported = typeof Notification !== "undefined";
  const canFire = permission === "granted";

  const updateRem = (patch) => {
    const next = { ...rem, ...patch };
    setRem(next);
    saveReminderPrefs(next);
    if (canFire) startReminderScheduler();
    else stopReminderScheduler();
  };

  const toggleEnabled = async () => {
    if (!rem.enabled) {
      if (permission !== "granted") {
        const p = await requestNotificationPermission();
        setPermission(p);
        if (p !== "granted") {
          toast.error("Permesso notifiche negato dal browser");
          return;
        }
      }
      updateRem({ enabled: true });
      toast.success("Promemoria attivati");
    } else {
      updateRem({ enabled: false });
      stopReminderScheduler();
      toast.info("Promemoria disattivati");
    }
  };

  const toggleDay = (d) => {
    const days = new Set(rem.days || []);
    days.has(d) ? days.delete(d) : days.add(d);
    updateRem({ days: Array.from(days).sort() });
  };

  return (
    <div className="border border-[#2A2A35] p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-[#FF3B30]" />
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB]">Promemoria allenamento</div>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={!supported}
          data-testid="reminders-toggle"
          className={`border px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest flex items-center gap-2 transition-all ${
            rem.enabled && canFire
              ? "bg-[#E2FF31] border-[#E2FF31] text-black"
              : "border-[#2A2A35] text-[#A0A0AB] hover:border-white hover:text-white"
          }`}
        >
          {rem.enabled && canFire ? <><BellRing className="w-3 h-3" /> ATTIVI</> : <><BellOff className="w-3 h-3" /> INATTIVI</>}
        </button>
      </div>

      {!supported ? (
        <div className="text-sm text-[#A0A0AB]">Il tuo browser non supporta le notifiche.</div>
      ) : permission === "denied" ? (
        <div className="text-sm text-[#A0A0AB]">
          Le notifiche sono bloccate. Sbloccarle dalle impostazioni del browser per attivare i promemoria.
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Giorni</div>
            <div className="flex flex-wrap gap-1">
              {DAYS_IT.map((d) => {
                const on = (rem.days || []).includes(d.key);
                return (
                  <button
                    key={d.key}
                    onClick={() => toggleDay(d.key)}
                    disabled={!rem.enabled}
                    data-testid={`rem-day-${d.key}`}
                    className={`px-3 py-2 text-[10px] font-bold uppercase tracking-widest border transition-all disabled:opacity-40 ${
                      on ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#2A2A35] text-[#A0A0AB] hover:border-white"
                    }`}
                  >{d.label}</button>
                );
              })}
            </div>
          </div>
          <div className="flex items-end gap-4 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">Orario</div>
              <input
                type="time"
                value={rem.time || "18:00"}
                onChange={(e) => updateRem({ time: e.target.value })}
                disabled={!rem.enabled}
                data-testid="rem-time"
                className="bg-[#141419] border border-[#2A2A35] px-3 py-2 focus:border-[#FF3B30] focus:outline-none font-mono-alt disabled:opacity-40"
              />
            </div>
            <button
              onClick={testNotification}
              disabled={!canFire}
              data-testid="rem-test"
              className="border border-[#2A2A35] hover:border-[#E2FF31] hover:text-[#E2FF31] text-[10px] font-bold uppercase tracking-widest px-4 py-2 disabled:opacity-40"
            >
              Invia test
            </button>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB]">
            I promemoria arrivano quando l'app è aperta in una scheda del browser.
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, suffix, testid }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-2">{label}</div>
      <div className="flex items-baseline gap-2 border border-[#2A2A35] focus-within:border-[#FF3B30] transition-colors">
        <input
          type="number"
          step="0.1"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className="flex-1 bg-[#141419] px-3 py-2 focus:outline-none text-sm w-full"
        />
        {suffix && <span className="text-[10px] uppercase tracking-widest text-[#A0A0AB] pr-3">{suffix}</span>}
      </div>
    </div>
  );
}
